import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons/faEllipsisVertical";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../../components/AtiraIcon";
import { Clickable } from "../../../../components/Clickable";
import { Flex } from "../../../../components/Flex";
import { Text } from "../../../../components/Text";
import { WarningModal } from "../../../../components/WarningModal";
import { ConversationMessage } from "../../../../model/integration/dto/meta/MetaConversationMessage";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { Rounded } from "../../../../theme/Rounded";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { Dropdown, MenuProps } from "../../../../utils/local-antd";
import { useWAMessageReaction } from "./useWAMessageReaction";
import { TestIDs } from "../../../../tests/types/testIds";
import {
  downloadMessageMedia,
  isDownloadable,
} from "./components/media-download";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

const Slot = styled.div<{ $isSender: boolean; $open: boolean }>`
  position: absolute;
  top: 0;
  ${({ $isSender }) =>
    $isSender
      ? "inset-inline-start: -2rem; padding-inline-end: 0.5rem;"
      : "inset-inline-end: -2rem; padding-inline-start: 0.5rem;"}
  color: ${({ theme }) => theme.darkerSub};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transition: opacity 0.15s ease;
`;

const Trigger = styled(Clickable)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: ${Rounded.circle};
  color: inherit;
  opacity: 0.65;

  &:hover {
    opacity: 1;
    background-color: rgba(128, 128, 128, 0.18);
  }
`;

const EmojiRow = styled(Flex)`
  gap: 2px;
`;

const EmojiButton = styled(Clickable)`
  padding: 2px 4px;
  border-radius: ${Rounded.sm};
  font-size: 1.1rem;
  line-height: 1;

  &:hover {
    background-color: ${({ theme }) => theme.sub};
  }
`;

type Props = {
  message: ConversationMessage;
  isSender: boolean;
  /** Absent for a message WhatsApp cannot quote, such as one still sending */
  onReply?: (message: ConversationMessage) => void;
};

export const WAMessageMenu: React.FC<Props> = ({
  message,
  isSender,
  onReply,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );

  const { ownReaction, react } = useWAMessageReaction(message);

  const active = menuOpen || confirmOpen;

  const onDelete = async () => {
    try {
      setLoading(true);

      await dispatch(
        integrationActions.deleteWhatsappMetaMessage({
          userId,
          integrationId: integration!._id,
          conversationId: message.conversationId,
          messageId: message._id,
        }),
      ).unwrap();

      setConfirmOpen(false);
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  const items: MenuProps["items"] = [
    {
      key: "react",
      label: (
        <EmojiRow onClick={(event) => event.stopPropagation()}>
          {QUICK_REACTIONS.map((emoji) => (
            <EmojiButton
              key={emoji}
              onClick={() => {
                react(emoji);
                setMenuOpen(false);
              }}
            >
              {ownReaction?.emoji === emoji ? `${emoji} ✓` : emoji}
            </EmojiButton>
          ))}
        </EmojiRow>
      ),
    },
    ...(onReply && message.metaMessageId
      ? [
          { type: "divider" as const },
          {
            key: "reply",
            label: <Text>{t("integration.whatsapp.meta.reply.action")}</Text>,
            onClick: () => {
              onReply(message);
              setMenuOpen(false);
            },
          },
        ]
      : []),
    ...(isDownloadable(message)
      ? [
          { type: "divider" as const },
          {
            key: "download",
            label: <Text>{t("common.download")}</Text>,
            onClick: () => {
              downloadMessageMedia(message);
              setMenuOpen(false);
            },
          },
        ]
      : []),
    { type: "divider" },
    {
      key: "delete",
      label: (
        <span
          data-testid={TestIDs.integration.whatsappMeta.chat.messageMenu.delete}
        >
          <Text color="danger">{t("common.delete")}</Text>
        </span>
      ),
      onClick: () => setConfirmOpen(true),
    },
  ];

  return (
    <Slot
      className="atira-wa-message-menu"
      $isSender={isSender}
      $open={active}
      onClick={(event) => event.stopPropagation()}
    >
      <Dropdown
        menu={{ items }}
        trigger={["click"]}
        placement="bottomRight"
        arrow={{ pointAtCenter: true }}
        open={menuOpen}
        onOpenChange={setMenuOpen}
        destroyOnHidden
      >
        <Trigger
          dataTestId={TestIDs.integration.whatsappMeta.chat.messageMenu.open}
        >
          <AtiraIcon icon={faEllipsisVertical} size="sm" />
        </Trigger>
      </Dropdown>

      {confirmOpen ? (
        <WarningModal
          isOpen={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={onDelete}
          loading={loading}
          confirmButtonDataTestId={
            TestIDs.integration.whatsappMeta.chat.messageMenu.confirmDelete
          }
          title={t("integration.whatsapp.meta.message.delete.title")}
          description={t(
            "integration.whatsapp.meta.message.delete.description",
          )}
        />
      ) : null}
    </Slot>
  );
};
